import { BittsSegment } from '@crossbeam/bitts';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import useRequests from '@/composables/useRequests';

const VALID_TABS = ['all', 'inbox', 'sent'] as const;
const VALID_VIEWS = ['all', 'partnerships', 'data_share'] as const;

type TabType = (typeof VALID_TABS)[number];
type ViewType = (typeof VALID_VIEWS)[number];

type RequestsDrawerOptionsType = {
  openRouteName: string;
  closeRouteName: string;
  acceptRouteName: string;
  defaultView?: ViewType;
};

const VIEW_OPTIONS = [
  { value: 'all' as ViewType, label: 'All' },
  { value: 'partnerships' as ViewType, label: 'Partnership' },
  { value: 'data_share' as ViewType, label: 'Data sharing' },
];

export default function useRequestsDrawer(options: RequestsDrawerOptionsType) {
  const route = useRoute();
  const router = useRouter();
  const {
    allPendingInboundDataShareRequests,
    allPendingOutboundDataShareRequests,
    allPendingOutboundPartnershipProposals,
    declinedOutboundPartnershipProposals,
    allPendingInboundPartnershipProposals,
  } = useRequests();

  function isRequestsRoute(name: unknown) {
    return name === options.openRouteName || name === options.acceptRouteName;
  }

  const initialTab: TabType = VALID_TABS.includes(route?.query?.tab as TabType)
    ? (route.query.tab as TabType)
    : 'inbox';

  const fallbackView = options.defaultView ?? 'partnerships';
  const initialView: ViewType = VALID_VIEWS.includes(
    route?.query?.view as ViewType,
  )
    ? (route.query.view as ViewType)
    : fallbackView;

  const isVisible = ref(isRequestsRoute(route.name));

  watch(
    () => route.name,
    (name) => {
      isVisible.value = isRequestsRoute(name);
    },
  );

  const tab = ref<TabType>(initialTab);
  const view = ref<ViewType>(initialView);
  const segmentRef = ref<InstanceType<typeof BittsSegment> | null>(null);

  // initialTab/initialView read the query only at setup, but this composable is
  // reused across routes, so a deep-link landing while already mounted must
  // re-sync. Ignores invalid values, so open()/close() never reset the tab.
  watch(
    () => [route.query.tab, route.query.view],
    ([qTab, qView]) => {
      if (VALID_VIEWS.includes(qView as ViewType)) {
        view.value = qView as ViewType;
      }
      if (VALID_TABS.includes(qTab as TabType)) {
        tab.value = qTab as TabType;
        const index = VALID_TABS.indexOf(qTab as TabType);
        segmentRef.value?.toggleSegment?.(qTab as TabType, index, false);
      }
    },
  );

  // The drawer uses destroy-on-close, so BittsSegment is torn down and rebuilt
  // on every reopen and re-reads this as its initial active index. The `tab`
  // ref persists across open/close (it drives the content), so this must track
  // the live tab — otherwise the segment snaps back to Inbox while the content
  // still shows the last tab.
  const currentTabIndex = computed(() => {
    const index = VALID_TABS.indexOf(tab.value);
    return index >= 0 ? index : 0;
  });

  const inboxCount = computed(() => {
    if (view.value === 'all')
      return (
        allPendingInboundPartnershipProposals.value.length +
        allPendingInboundDataShareRequests.value.length
      );
    if (view.value === 'partnerships')
      return allPendingInboundPartnershipProposals.value.length;
    return allPendingInboundDataShareRequests.value.length;
  });

  // Declined outbound proposals surface on the Sent/All tabs (so the sender can
  // read the reason), so they count toward the Sent tab even though they're no
  // longer "pending" — otherwise the tab is disabled and the reason is
  // unreachable.
  const sentCount = computed(() => {
    if (view.value === 'all')
      return (
        allPendingOutboundPartnershipProposals.value.length +
        declinedOutboundPartnershipProposals.value.length +
        allPendingOutboundDataShareRequests.value.length
      );
    if (view.value === 'partnerships')
      return (
        allPendingOutboundPartnershipProposals.value.length +
        declinedOutboundPartnershipProposals.value.length
      );
    return allPendingOutboundDataShareRequests.value.length;
  });

  const totalCount = computed(() => inboxCount.value + sentCount.value);

  const tabs = computed(() => [
    {
      title: 'All',
      id: 'all' as TabType,
      count: totalCount.value,
      disabled: !totalCount.value,
    },
    {
      title: 'Inbox',
      id: 'inbox' as TabType,
      count: inboxCount.value,
      disabled: !inboxCount.value,
    },
    {
      title: 'Sent',
      id: 'sent' as TabType,
      count: sentCount.value,
      disabled: !sentCount.value,
    },
  ]);

  const badgeCount = computed(() => {
    if (fallbackView === 'all')
      return (
        allPendingInboundPartnershipProposals.value.length +
        allPendingInboundDataShareRequests.value.length
      );
    if (fallbackView === 'partnerships')
      return allPendingInboundPartnershipProposals.value.length;
    return allPendingInboundDataShareRequests.value.length;
  });

  /**
   * Visibility is read from where navigation lands, so a leave guard that
   * blocks it (such as the Sharing Settings unsaved changes prompt) leaves the
   * drawer as it was.
   */
  async function navigate(name: string) {
    await router.push({ name });
    isVisible.value = isRequestsRoute(route.name);
  }

  function open() {
    return navigate(options.openRouteName);
  }

  function close() {
    return navigate(options.closeRouteName);
  }

  function handleTabChange(newTab: TabType) {
    tab.value = newTab;
  }

  function handleWidgetTabReset(newTab: TabType) {
    tab.value = newTab;
    const index = VALID_TABS.indexOf(newTab);
    if (index >= 0) {
      segmentRef.value?.toggleSegment(newTab, index, false);
    }
  }

  return {
    isVisible,
    tab,
    view,
    segmentRef,
    tabs,
    viewOptions: VIEW_OPTIONS,
    initialTabIndex: currentTabIndex,
    badgeCount,
    open,
    close,
    handleTabChange,
    handleWidgetTabReset,
  };
}
